import React from "react";

/**
 * BulkDeleteConfirmDialog Component
 * 
 * Displays a confirmation dialog for bulk delete operations.
 * Shows the list of items to be deleted and requires user confirmation.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onClose - Handler to close the dialog
 * @param {Function} props.onConfirm - Handler for confirm action
 * @param {Array} props.items - Array of items to delete
 * @param {Object} props.summary - Summary information about the deletion
 * @param {string} props.itemType - Type of items being deleted
 * @param {boolean} props.isLoading - Whether the deletion is in progress
 * @returns {JSX.Element|null} The confirmation dialog component or null if not open
 */
const BulkDeleteConfirmDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  items, 
  summary,
  itemType = "item",
  isLoading = false
}) => {
  if (!isOpen) return null;
  
  const itemCount = items.length;
  const itemLabel = itemCount > 1 ? `${itemType}s` : itemType;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">
          Confirm Bulk Delete
        </h3>
        
        <p className="text-gray-700 mb-4">
          Are you sure you want to delete{' '}
          <span className="font-semibold">{itemCount}</span>{' '}
          {itemLabel}?
          {summary?.totalServices > 0 && (
            <> This will affect{' '}
              <span className="font-semibold">{summary.totalServices}</span>{' '}
              service{summary.totalServices > 1 ? 's' : ''}.
            </>
          )}
        </p>
        
        <div className="mb-4 max-h-40 overflow-y-auto">
          <p className="font-medium text-sm text-gray-600 mb-2">
            {itemType === "category" ? "Categories" : 
             itemType === "subcategory" ? "Subcategories" : 
             "Items"} to delete:
          </p>
          <ul className="space-y-1 bg-gray-50 rounded p-3">
            {items.map((item, idx) => (
              <li key={idx} className="text-sm text-gray-700 flex items-start">
                <span className="text-gray-400 mr-2">•</span>
                <span className="flex-1">
                  {item.name || item}
                  {item.count !== undefined && (
                    <span className="text-gray-500 ml-1">
                      ({item.count} service{item.count !== 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
          <div className="flex">
            <svg
              className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-yellow-800">
              {itemType === "category" || itemType === "subcategory" ? (
                <>All services will be moved to the recycle bin and can be restored later.</>
              ) : (
                <>This action will move items to the recycle bin.</>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Deleting...</span>
              </>
            ) : (
              <span>Confirm Delete</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteConfirmDialog;
